import { useState, useEffect, useContext, useRef } from "react";
import { useRecoilState } from "recoil";
import { activeSlideSelector } from "../../atom";
import { ViewerContext } from "../../page/Viewer";

import styled from "styled-components";
import { Modal, Input, Button, Flex, Form, Select, Tooltip, Radio } from "antd";
import { DeleteOutlined, LoadingOutlined, PlusCircleOutlined, EditOutlined, CloseOutlined } from "@ant-design/icons";

import { deleteComment, getSlideData, postComment, putComment, patchSlideData, downloadSlide, getSlideInfo, addSlideInfo, updateSlideInfo, deleteSlideInfo } from "../../api/slideAPI";
import { createTempLinkForAdmin } from "../../api/templinkAPI";
import { useAnnotationInfo } from "../../hooks/useAnnotationInfo";
import { AnnotationInfoPanel as AnnotationInfoPanelComponent } from "./AnnotationInfoPanel";
import FileSaver from "file-saver";
import { getLoginUser } from "../../api/user";
import TextArea from "antd/es/input/TextArea";

interface SideMenuProps {
  activePanel: string | null;
  onClose: () => void;
  postWriter?: string;
  postId?: number;
}

interface User {
  username: string;
  role: string;
}

const SideMenuSample2 = ({ activePanel, onClose, postWriter, postId }: SideMenuProps) => {
  const { userCustomSet } = useContext(ViewerContext);
  const [currentSlide, setCurrentSlide] = useRecoilState(activeSlideSelector);
  const [currentUser, setCurrentUser] = useState<User>({ username: "", role: "" });

  const annotationInfo = useAnnotationInfo({ currentSlide, setCurrentSlide });

  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showCommentModal, setShowCommentModal] = useState<boolean>(false);
  const [commentContent, setCommentContent] = useState<string>("");
  const [currentCommentId, setCurrentCommentId] = useState<number>(0);
  const [showDownloadBtn, setShowDownloadBtn] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [demoUrl, setDemoUrl] = useState<string>("");
  const [isCopied, setIsCopied] = useState(false);
  const demoUrlInputRef = useRef<HTMLInputElement>(null);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoPassword, setDemoPassword] = useState<string>("");
  const [demoExpiredAt, setDemoExpiredAt] = useState<string>("");
  const [isDemoGenerating, setIsDemoGenerating] = useState(false);
  const [demoTarget, setDemoTarget] = useState<'viewer' | 'consultation'>('viewer');

  const [showSlideInfoModal, setShowSlideInfoModal] = useState(false);
  const [slideInfoList, setSlideInfoList] = useState<any[]>([]);
  const [currentInfoId, setCurrentInfoId] = useState<number>(0);
  const [pathologyNumber, setPathologyNumber] = useState<string>("");
  const [slideMemo, setSlideMemo] = useState<string>("");
  

  const [form] = Form.useForm<any>();

  const editSlideInformation = async (values: any) => {
    if (!currentSlide?.slideId) return;
    values.slideId = currentSlide.slideId;
    await patchSlideData(values);

    const data = await getSlideData(currentSlide.slideId);

    if (!userCustomSet?.useAnnotation) {
      data.annotations = [];
    }
    setCurrentSlide({ ...currentSlide, ...data });

    setShowEditModal(false);
  };

  const cancelEdit = async () => {
    form.resetFields();
    setShowEditModal(false);
  };

  const handleDownload = async () => {
    if (!currentSlide?.slideId) return;
    setIsDownloading(true);
    const fileData = await downloadSlide(currentSlide.slideId);
    if(fileData === false) {
      setIsDownloading(false);
      window.alert("다운로드 권한이 없습니다.");
      return;
    }
    FileSaver.saveAs(fileData, currentSlide.filename);
    setIsDownloading(false);
  };

  const handleGenerateDemoUrl = async () => {
    if (!postId) return;
    setIsDemoGenerating(true);
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 0, 0);
    const expiredAtIso = demoExpiredAt ? `${demoExpiredAt}:00` : endOfToday.toISOString();
    const uuid = await createTempLinkForAdmin({ postId, ...(demoPassword ? { password: demoPassword } : {}), expiredAt: expiredAtIso });
    const base = process.env.REACT_APP_SERVICE_URL || window.location.origin;
    const linkParams = new URLSearchParams();
    linkParams.set("target", demoTarget);
    const url = uuid ? `${base}/temp-link/${uuid}?${linkParams.toString()}` : "";
    setDemoUrl(url);
    setIsCopied(false);
    setIsDemoGenerating(false);
    setShowDemoModal(false);
  };

  const handleCopyDemoUrl = () => {
    if (!demoUrl) return;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(demoUrl).then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = demoUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleEditAnnotationFromSidebar = (annotation: any) => {
    if((annotation.writer !== currentUser.username) && (currentUser.role !== "ADMIN_MAIN")) {
      window.alert("수정 권한이 없습니다.");
      return;
    }
    const latestAnnotation = currentSlide?.annotations?.find(
      (anno: any) => anno.annoId === annotation.annoId
    );

    if (latestAnnotation) {
      annotationInfo.setSelectedAnnotation(latestAnnotation);

      setTimeout(() => {
        annotationInfo.setShowEditModal(true);
      }, 0);
    }
  };

  const addComment = async () => {
    if (!currentSlide?.slideId) return;
    if (commentContent !== "") {
      currentCommentId === 0
        ? await postComment(currentSlide.slideId, commentContent)
        : await putComment(currentCommentId, commentContent);

      const data = await getSlideData(currentSlide.slideId);
      setCurrentSlide({ ...currentSlide, ...data });
    }
    setShowCommentModal(false);
    setCommentContent("");
  };

  const clickEditComment = async (comment: any) => {
    if ((currentUser.username !== comment.writer) && (currentUser.role !== "ADMIN_MAIN")) {
      window.alert("수정 권한이 없습니다.");
      return;
    }
    setCommentContent(comment.content);
    setCurrentCommentId(comment.slideCommentId);
    setShowCommentModal(true);
  };

  const clickDeleteComment = async (comment: any) => {
    if (!currentSlide?.slideId) return;
    if ((currentUser.username !== comment.writer) && (currentUser.role !== "ADMIN_MAIN")) {
      window.alert("삭제 권한이 없습니다.");
      return;
    }
    if (window.confirm(`해당 코멘트를 삭제하시겠습니까?`)) {
      await deleteComment(comment.slideCommentId);
      const data = await getSlideData(currentSlide.slideId);
      setCurrentSlide({ ...currentSlide, ...data });
    }
  };

  const fetchSlideInfo = async () => {
    if (currentSlide?.slideId && currentUser.role === "ADMIN_MAIN") {
      try {
        const data = await getSlideInfo(currentSlide.slideId);
        setSlideInfoList(data || []);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const saveSlideInfo = async () => {
    if (!currentSlide?.slideId) return;
    if (!pathologyNumber.trim()) {
      window.alert("Pathology Number를 입력해주세요.");
      return;
    }
    try {
      if (currentInfoId === 0) {
        await addSlideInfo(currentSlide.slideId, pathologyNumber, slideMemo);
      } else {
        await updateSlideInfo(currentInfoId, pathologyNumber, slideMemo);
      }
      await fetchSlideInfo();
      setShowSlideInfoModal(false);
      setPathologyNumber("");
      setSlideMemo("");
      setCurrentInfoId(0);
    } catch (e: any) {
      window.alert(e.message);
    }
  };

  const clickEditSlideInfo = (info: any) => {
    setPathologyNumber(info.pathologyNumber);
    setSlideMemo(info.memo);
    setCurrentInfoId(info.id);
    setShowSlideInfoModal(true);
  };

  const clickDeleteSlideInfo = async (infoId: number) => {
    if (window.confirm("해당 Pathology Information을 삭제하시겠습니까?")) {
      try {
        await deleteSlideInfo(infoId);
        await fetchSlideInfo();
      } catch (e: any) {
        window.alert(e.message);
      }
    }
  };

  useEffect(() => {
    getLoginUser().then(data => {
      setShowDownloadBtn(true);
      setCurrentUser({ username: data.name, role: data.role });
    });
  }, []);


  const DisplayColor = (props: any) => {
    const { annotation } = props;
    const annoInfo = annotation?.annoInfo;

    let color;
    if (annoInfo && annoInfo[0] === 'g' && annoInfo[1]?.class === 'annotation arrow-group') {
      color = annoInfo[1]?.stroke || '#285F3D';
    } else {
      color = annoInfo?.[1]?.stroke;
    }

    const style = {
      width: "1rem",
      height: "1rem",
      backgroundColor: color,
      borderRadius: "3px",
      marginLeft: "3px",
    };

    return (
      <>
        <div style={style}></div>
      </>
    );
  };

  useEffect(() => {
    fetchSlideInfo();
  }, [currentSlide, currentUser.role]);

  const renderContent = () => {
    switch (activePanel) {
      case 'info':
        return (
          <>
            <PanelHeader>
              <PanelTitle>INFORMATION</PanelTitle>
              <CloseButton onClick={onClose}>×</CloseButton>
            </PanelHeader>
            <PanelContent>
              <InfoGrid>
                <StInfoLabel>Slide Name</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.slidename : "-"}</StInfoValue>
                <StInfoLabel>Caption</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.caption : "-"}</StInfoValue>
                <StInfoLabel>Gender</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.gender : "-"}</StInfoValue>
                <StInfoLabel>Age</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.age : "-"}</StInfoValue>
                <StInfoLabel>Site</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.site : "-"}</StInfoValue>
                <StInfoLabel>Diagnosis</StInfoLabel>
                <StInfoValue>{currentSlide ? currentSlide.zindanName : "-"}</StInfoValue>
              </InfoGrid>
              <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                {postWriter === currentUser.username &&
                  <Button onClick={() => setShowEditModal(true)} block>Edit Information</Button>
                }
                {showDownloadBtn && (
                  <Button onClick={handleDownload} block>Download File</Button>
                )}
              </div>
              {(currentUser.role === "ADMIN_MAIN" || postWriter === currentUser.username) && postId && (
                <div style={{ marginTop: '15px' }}>
                  <Button onClick={() => { setDemoPassword(""); setDemoExpiredAt(""); setDemoTarget('viewer'); setShowDemoModal(true); }} block>데모 URL 생성</Button>
                  {demoUrl && (
                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        ref={demoUrlInputRef}
                        readOnly
                        value={demoUrl}
                        style={{
                          flex: 1,
                          background: '#051e34',
                          border: '1px solid #1e3a52',
                          color: '#8899aa',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                      <Button size="small" onClick={handleCopyDemoUrl}>
                        {isCopied ? '복사됨' : '복사'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </PanelContent>
            {currentUser.role === "ADMIN_MAIN" && postId && (
              <>
                <PanelHeader>
                  <PanelTitle>PATHOLOGY INFORMATION</PanelTitle>
                  <Tooltip title="이 패널은 관리자에게만 보입니다." placement="top">
                      <div style={{
                        fontSize: "0.65rem",
                        color: "#fff",
                        backgroundColor: "#942404",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontWeight: "bold",
                        letterSpacing: "0.5px",
                        cursor: "help"
                      }}>
                        ADMIN ONLY
                      </div>
                    </Tooltip>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#8899aa' }}>
                    <PlusCircleOutlined
                      onClick={() => {
                        setCurrentInfoId(0);
                        setPathologyNumber("");
                        setSlideMemo("");
                        setShowSlideInfoModal(true);
                      }}
                      style={{ fontSize: "1.2rem", cursor: "pointer" }}
                    />
                  </div>
                </PanelHeader>
                <PanelContent>
                  {slideInfoList.map((info) => (
                    <StInfoBox key={info.infoId}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: "0.95rem", color: "#fff", fontWeight: "bold", marginBottom: "0.3rem" }}>
                            {info.pathologyNumber}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: "#8899aa", lineHeight: "1.4", wordBreak: "break-word" }}>
                            {info.memo}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                          <EditOutlined
                            onClick={() => clickEditSlideInfo(info)}
                            style={{ fontSize: "1rem", cursor: "pointer", color: "#8899aa" }}
                          />
                          <DeleteOutlined
                            onClick={() => clickDeleteSlideInfo(info.id)}
                            style={{ fontSize: "1rem", cursor: "pointer", color: "#8899aa" }}
                          />
                        </div>
                      </div>
                    </StInfoBox>
                  ))}
                </PanelContent>
              </>
            )}
          </>
        );
      
      case 'annotation':
        return (
          <>
            <PanelHeader>
              <PanelTitle>ANNOTATIONS</PanelTitle>
              <CloseButton onClick={onClose}>×</CloseButton>
            </PanelHeader>
            <PanelContent>
              {(currentSlide?.annotations || []).map((annotation, idx) => (
                <StAnnotationBox key={`${idx}_${annotation.annoId}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.8rem" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.3rem" }}>
                        <DisplayColor annotation={annotation} />
                        <div 
                          title={`${annotation.content}`}
                          style={{
                            fontSize: "1rem",
                            fontWeight: "600",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth:"10rem",
                          color: "#fff"
                        }}
                        >
                          {annotation.content}
                        </div>
                        <div style={{
                          fontSize: "0.75rem",
                          color: "#1890ff",
                          padding: "2px 6px",
                          backgroundColor: "#051e34",
                          border: "1px solid #1e3a52",
                          borderRadius: "3px",
                          flexShrink: 0
                        }}>
                          {annotationInfo.getShapeType(annotation)}
                        </div>
                      </div>
                      <div style={{ fontSize: "0.85rem", color: "#8899aa", marginLeft: "1.5rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                        {annotation.writer}
                        {annotation.anonymous === true && (
                          <span style={{ fontSize: "0.75rem", color: "#8899aa", fontWeight: "normal" }}>(링크 접근 사용자)</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                      <EditOutlined
                        onClick={() => handleEditAnnotationFromSidebar(annotation)}
                        style={{ fontSize: "1.1rem", cursor: "pointer", color: "#8899aa" }}
                      />
                      <DeleteOutlined
                        onClick={() => {
                          annotationInfo.handleDeleteAnnotation(annotation);
                        }}
                        style={{ fontSize: "1.1rem", cursor: "pointer", color: "#8899aa" }}
                      />
                    </div>
                  </div>
                </StAnnotationBox>
              ))}
            </PanelContent>
          </>
        );
      
      case 'comment':
        return (
          <>
            <PanelHeader>
              <PanelTitle>COMMENTS</PanelTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#8899aa' }}>
                <PlusCircleOutlined
                  onClick={() => {
                    setCurrentCommentId(0);
                    setCommentContent("");
                    setShowCommentModal(true);
                  }}
                  style={{ fontSize: "1.2rem", cursor: "pointer" }}
                />
                <CloseButton onClick={onClose}>×</CloseButton>
              </div>
            </PanelHeader>
            <PanelContent>
              {(currentSlide?.slideComments || []).map((comment) => (
                <StCommentBox key={comment.slideCommentId}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                    <div style={{ fontSize: "0.95rem", color: "#fff", lineHeight: "1.5", wordBreak: "break-word", whiteSpace: "pre-wrap", flex: 1, marginBottom: "0.4rem" }}>
                      {comment.content}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                      <EditOutlined
                        onClick={() => clickEditComment(comment)}
                        style={{ fontSize: "1rem", cursor: "pointer", color: "#8899aa" }}
                      />
                      <DeleteOutlined
                        onClick={() => clickDeleteComment(comment)}
                        style={{ fontSize: "1rem", cursor: "pointer", color: "#8899aa" }}
                      />
                    </div>
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#8899aa", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                    {comment.writer}
                    {comment.anonymous === true && (
                      <span style={{ fontSize: "0.75rem", color: "#8899aa", fontWeight: "normal" }}>(링크 접근 사용자)</span>
                    )}
                  </div>
                </StCommentBox>
              ))}
            </PanelContent>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <StMainWrapper>
      {renderContent()}
      
      {/* Modals */}
      <Modal
        centered
        title="Edit Information"
        open={showEditModal}
        onCancel={cancelEdit}
        footer={null}
      >
        <Flex gap="middle" vertical>
          <Form
            form={form}
            onFinish={editSlideInformation}
            initialValues={currentSlide || {}}
            layout="vertical"
          >
            <Form.Item
              name="slidename"
              label="Slide Name"
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="caption"
              label="Caption"
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="gender"
              label="Gender"
            >
              <Select>
                <Select.Option value="M">Male</Select.Option>
                <Select.Option value="F">Female</Select.Option>
                <Select.Option value="U">Unknown</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="age"
              label="Age"
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="site"
              label="Site"
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="zindanName"
              label="Diagnosis"
            >
              <Input />
            </Form.Item>
            <Form.Item style={{ marginTop: "20px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={cancelEdit} style={{ marginRight: "10px" }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Flex>
      </Modal>
      <Modal
        title={currentCommentId === 0 ? "Add Comment" : "Edit Comment"}
        open={showCommentModal}
        onOk={addComment}
        onCancel={() => {
          setShowCommentModal(false);
          setCommentContent("");
        }}
        width={480}
        style={{ position: "absolute", top: 100, right: 16, margin: 0 }}
      >
        <TextArea
          value={commentContent}
          onChange={(e) => setCommentContent(e.target.value)}
          autoSize={{ minRows: 8, maxRows: 14 }}
          placeholder="Please input your comment"
        />
      </Modal>

      {/* 어노테이션 수정 모달 */}
      <AnnotationInfoPanelComponent {...annotationInfo} />

      <Modal
        centered
        title="데모 URL 생성"
        open={showDemoModal}
        onCancel={() => setShowDemoModal(false)}
        onOk={handleGenerateDemoUrl}
        okText="생성"
        cancelText="취소"
        confirmLoading={isDemoGenerating}
      >
        <Flex gap="middle" vertical style={{ marginTop: '8px' }}>
          <div>
            <div style={{ marginBottom: '6px', color: '#666', fontSize: '0.85rem' }}>공유 모드</div>
            <Radio.Group value={demoTarget} onChange={(e) => setDemoTarget(e.target.value)}>
              <Radio value="viewer">읽기 전용</Radio>
              <Radio value="consultation">어노테이션/코멘트 편집 가능</Radio>
            </Radio.Group>
          </div>
          <div>
            <div style={{ marginBottom: '6px', color: '#666', fontSize: '0.85rem' }}>비밀번호 (선택)</div>
            <Input.Password
              value={demoPassword}
              onChange={(e) => setDemoPassword(e.target.value)}
              placeholder="비밀번호 없이 접근 가능하게 하려면 비워두세요"
            />
          </div>
          <div>
            <div style={{ marginBottom: '6px', color: '#666', fontSize: '0.85rem' }}>만료일 (선택, 기본: 내일)</div>
            <Input
              type="datetime-local"
              value={demoExpiredAt}
              onChange={(e) => {
                const selected = e.target.value;
                if (!selected) { setDemoExpiredAt(""); return; }
                const today = new Date().toISOString().slice(0, 10);
                const selectedDate = selected.slice(0, 10);
                setDemoExpiredAt(selectedDate === today ? `${today}T23:59` : selected);
              }}
            />
          </div>
        </Flex>
      </Modal>

      {/* Pathology Information 모달 */}
      <Modal
        centered
        title={currentInfoId === 0 ? "Add Pathology Information" : "Edit Pathology Information"}
        open={showSlideInfoModal}
        onOk={saveSlideInfo}
        onCancel={() => {
          setShowSlideInfoModal(false);
          setPathologyNumber("");
          setSlideMemo("");
        }}
        okText="Save"
      >
        <Flex vertical gap="small" style={{ marginTop: "10px" }}>
          <div>
            <div style={{ marginBottom: "5px", fontWeight: 500 }}>Pathology Number</div>
            <Input
              value={pathologyNumber}
              onChange={(e) => setPathologyNumber(e.target.value)}
              placeholder="Pathology Number를 입력하세요"
            />
          </div>
          <div>
            <div style={{ marginBottom: "5px", fontWeight: 500 }}>Memo</div>
            <Input.TextArea
              value={slideMemo}
              onChange={(e) => setSlideMemo(e.target.value)}
              placeholder="메모를 입력하세요"
              rows={4}
            />
          </div>
        </Flex>
      </Modal>

      {isDownloading &&
        <Modal open={isDownloading} centered closable={false} footer={null}>
          Downloading...  <LoadingOutlined />
        </Modal>
      }
    </StMainWrapper>
  );
};

const StMainWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StAnnotationBox = styled.div`
  padding: 0.4rem;
  border: 1px solid #1e3a52;
  margin-bottom: 0.5rem;
  background-color: #051e34;
  border-radius: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StCommentBox = styled.div`
  padding: 0.8rem;
  background-color: #051e34;
  border-radius: 8px;
  margin-bottom: 0.6rem;
  border-left: 3px solid #1890ff;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StInfoLabel = styled.div`
  border-right: 1px solid #1e3a52;
  font-size: 1rem;
  font-weight: 600;
  color: #8899aa;
`;

const StInfoValue = styled.div`
  margin-left: 1rem;
  color: #fff;
`;

const PanelHeader = styled.div`
  padding: 15px 20px;
  border-bottom: 1px solid #1e3a52;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #051e34;
  flex-shrink: 0;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #8899aa;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #fff;
  }
`;

const PanelContent = styled.div`
  padding: 15px;
  overflow-y: auto;
  flex: 1;
  background-color: #0a1929;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #051e34;
  }

  &::-webkit-scrollbar-thumb {
    background: #1e3a52;
    border-radius: 3px;

    &:hover {
      background: #2d5171;
    }
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 8rem auto;
  row-gap: 10px;
`;

const StInfoBox = styled.div`
  padding: 0.8rem;
  background-color: #051e34;
  border-radius: 8px;
  margin-bottom: 0.6rem;
  border-left: 3px solid #52c41a;

  &:last-child {
    margin-bottom: 0;
  }
`;

// 이미지(Image/Gross)용 사이드 메뉴(ImageSideMenu)가 같은 모양을 쓰도록 공유
export {
  StMainWrapper,
  StAnnotationBox,
  PanelHeader,
  PanelTitle,
  CloseButton,
  PanelContent,
};

export default SideMenuSample2;
